import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Alert, Badge, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Pagination, Stack, Tooltip, Typography,
} from '@mui/material';
import {
    getBetupMatchIdByVirtualSyncId, getVirtualActivityLog, getVirtualCatalogSports, getVirtualPlayer,
    getVirtualProviderTeam, markAllVirtualActivityLogEntriesRead, markVirtualActivityLogEntryRead,
} from '../../Api';
import { VirtualActivityLogEntryDto, VirtualActivityLogLevel, VirtualProviderPlayerDto, VirtualProviderTeamDto } from '../../models/dtos/VirtualProviderCatalogDto';
import VirtualLeagueDetailsDialog from './VirtualLeagueDetailsDialog';
import VirtualTeamDetailsDialog from './VirtualTeamDetailsDialog';
import VirtualPlayerDetailsDialog from './VirtualPlayerDetailsDialog';

const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 30000;

function levelIcon(level: VirtualActivityLogLevel) {
    switch (level) {
        case 'ERROR': return <ErrorOutlineIcon color="error" fontSize="small" />;
        case 'WARNING': return <WarningAmberIcon color="warning" fontSize="small" />;
        default: return <InfoOutlinedIcon color="info" fontSize="small" />;
    }
}

function levelColor(level: VirtualActivityLogLevel): 'default' | 'warning' | 'error' {
    if (level === 'ERROR') return 'error';
    if (level === 'WARNING') return 'warning';
    return 'default';
}

export default function VirtualActivityLogBell() {
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = React.useState(0);
    const [open, setOpen] = React.useState(false);
    const [entries, setEntries] = React.useState<VirtualActivityLogEntryDto[]>([]);
    const [offset, setOffset] = React.useState(0);
    const [totalItems, setTotalItems] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');

    const [goToAnchor, setGoToAnchor] = React.useState<HTMLElement | null>(null);
    const [goToEntry, setGoToEntry] = React.useState<VirtualActivityLogEntryDto | null>(null);
    const [resolving, setResolving] = React.useState(false);

    const [sportNames, setSportNames] = React.useState<Map<number, string> | null>(null);
    const [openLeagueSyncId, setOpenLeagueSyncId] = React.useState<string | null>(null);
    const [openTeam, setOpenTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [openPlayer, setOpenPlayer] = React.useState<VirtualProviderPlayerDto | null>(null);

    const refreshUnreadCount = React.useCallback(() => {
        getVirtualActivityLog(0, 1).then(page => setUnreadCount(page.unreadCount)).catch(() => { /* best-effort */ });
    }, []);

    React.useEffect(() => {
        refreshUnreadCount();
        const intervalId = window.setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [refreshUnreadCount]);

    const loadEntries = React.useCallback((nextOffset: number) => {
        setLoading(true);
        setError('');
        getVirtualActivityLog(nextOffset, PAGE_SIZE)
            .then(page => {
                setEntries(page.items);
                setOffset(nextOffset);
                setTotalItems(page.totalItems);
                setUnreadCount(page.unreadCount);
            })
            .catch(reason => setError(reason instanceof Error ? reason.message : 'Could not load activity log'))
            .finally(() => setLoading(false));
    }, []);

    const openLog = () => {
        setOpen(true);
        loadEntries(0);
    };

    const markRead = async (entry: VirtualActivityLogEntryDto) => {
        if (entry.read) return;
        setEntries(current => current.map(e => (e.id === entry.id ? { ...e, read: true } : e)));
        setUnreadCount(count => Math.max(0, count - 1));
        try {
            await markVirtualActivityLogEntryRead(entry.id);
        } catch {
            // Best-effort: the badge will resync on the next poll if this failed.
        }
    };

    const markAllRead = async () => {
        setEntries(current => current.map(e => ({ ...e, read: true })));
        setUnreadCount(0);
        try {
            await markAllVirtualActivityLogEntriesRead();
        } catch {
            // Best-effort: the badge will resync on the next poll if this failed.
        }
    };

    const ensureSportNames = async (): Promise<Map<number, string>> => {
        if (sportNames) return sportNames;
        const sports = await getVirtualCatalogSports();
        const map = new Map(sports.map(sport => [sport.id, sport.name]));
        setSportNames(map);
        return map;
    };

    const closeGoToMenu = () => {
        setGoToAnchor(null);
        setGoToEntry(null);
    };

    const goToLeague = (leagueId: string) => {
        closeGoToMenu();
        setOpenLeagueSyncId(leagueId);
    };

    const goToTeam = async (teamId: string) => {
        closeGoToMenu();
        setResolving(true);
        try {
            const [team] = await Promise.all([getVirtualProviderTeam(teamId), ensureSportNames()]);
            setOpenTeam(team);
        } catch (reason) {
            setNotice(reason instanceof Error ? reason.message : 'Could not open team');
        } finally {
            setResolving(false);
        }
    };

    const goToPlayer = async (playerId: string) => {
        closeGoToMenu();
        setResolving(true);
        try {
            const [player] = await Promise.all([getVirtualPlayer(playerId), ensureSportNames()]);
            setOpenPlayer(player);
        } catch (reason) {
            setNotice(reason instanceof Error ? reason.message : 'Could not open player');
        } finally {
            setResolving(false);
        }
    };

    const goToMatch = async (matchId: string) => {
        closeGoToMenu();
        setResolving(true);
        try {
            const betupMatchId = await getBetupMatchIdByVirtualSyncId(matchId);
            if (betupMatchId == null) {
                setNotice('This match has not been synchronized to the main catalog yet.');
                return;
            }
            setOpen(false);
            navigate(`/matches/${betupMatchId}`);
        } finally {
            setResolving(false);
        }
    };

    const hasGoToOptions = (entry: VirtualActivityLogEntryDto) =>
        entry.leagueIds.length > 0 || entry.teamIds.length > 0 || entry.playerIds.length > 0 || entry.matchIds.length > 0;

    return (
        <>
            <Tooltip title="Virtual sports activity log">
                <IconButton onClick={openLog}>
                    <Badge color="error" badgeContent={unreadCount} max={99}>
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    Virtual sports activity log
                    {unreadCount > 0 ? <Chip size="small" color="error" label={`${unreadCount} unread`} sx={{ ml: 1 }} /> : null}
                </DialogTitle>
                <DialogContent dividers>
                    {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                    {notice ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}
                    {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box> : entries.length === 0 ? (
                        <Typography color="text.secondary">No activity yet.</Typography>
                    ) : (
                        <List disablePadding>
                            {entries.map(entry => (
                                <ListItem
                                    key={entry.id}
                                    divider
                                    onClick={() => markRead(entry)}
                                    sx={{ cursor: entry.read ? 'default' : 'pointer', bgcolor: entry.read ? 'transparent' : 'action.hover' }}
                                    secondaryAction={hasGoToOptions(entry) ? (
                                        <Button
                                            size="small"
                                            disabled={resolving}
                                            onClick={event => {
                                                event.stopPropagation();
                                                setGoToAnchor(event.currentTarget);
                                                setGoToEntry(entry);
                                            }}
                                        >
                                            Go to
                                        </Button>
                                    ) : undefined}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>{levelIcon(entry.level)}</ListItemIcon>
                                    <ListItemText
                                        primary={<Typography variant="body2" sx={{ fontWeight: entry.read ? 400 : 600 }}>{entry.message}</Typography>}
                                        secondary={
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Chip size="small" variant="outlined" color={levelColor(entry.level)} label={entry.level} />
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(entry.createdAt).toLocaleString()}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                    {totalItems > PAGE_SIZE ? (
                        <Pagination
                            sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
                            count={Math.ceil(totalItems / PAGE_SIZE)}
                            page={Math.floor(offset / PAGE_SIZE) + 1}
                            onChange={(_, page) => loadEntries((page - 1) * PAGE_SIZE)}
                        />
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={markAllRead} disabled={unreadCount === 0}>Mark all as read</Button>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Menu anchorEl={goToAnchor} open={Boolean(goToAnchor)} onClose={closeGoToMenu}>
                {goToEntry?.leagueIds.map(id => (
                    <MenuItem key={`league-${id}`} onClick={() => goToLeague(id)}>Go to league</MenuItem>
                ))}
                {goToEntry?.teamIds.map((id, index) => (
                    <MenuItem key={`team-${id}`} onClick={() => goToTeam(id)}>
                        {goToEntry.teamIds.length > 1 ? `Go to team ${index + 1}` : 'Go to team'}
                    </MenuItem>
                ))}
                {goToEntry?.playerIds.map((id, index) => (
                    <MenuItem key={`player-${id}`} onClick={() => goToPlayer(id)}>
                        {goToEntry.playerIds.length > 1 ? `Go to player ${index + 1}` : 'Go to player'}
                    </MenuItem>
                ))}
                {goToEntry?.matchIds.map(id => (
                    <MenuItem key={`match-${id}`} onClick={() => goToMatch(id)}>Go to match</MenuItem>
                ))}
            </Menu>

            <VirtualLeagueDetailsDialog
                leagueSyncId={openLeagueSyncId}
                onClose={() => setOpenLeagueSyncId(null)}
                onUpdated={() => { /* preview only from the activity log */ }}
            />
            <VirtualTeamDetailsDialog
                team={openTeam}
                sportNames={sportNames ?? new Map()}
                onClose={() => setOpenTeam(null)}
                onUpdated={() => { /* preview only from the activity log */ }}
            />
            <VirtualPlayerDetailsDialog
                player={openPlayer}
                sportNames={sportNames ?? new Map()}
                onClose={() => setOpenPlayer(null)}
                onUpdated={() => { /* preview only from the activity log */ }}
                onDeleted={() => setOpenPlayer(null)}
            />
        </>
    );
}
